import * as React from 'react';
import { getIntrinsicElementProps, mergeClasses, slot, useId } from '@fluentui/react-components';
import { useReferenceListContext_unstable } from '../../contexts/referenceListContext';
import { referenceExtraClassNames } from './useReferenceStyles.styles';
import type { ReferenceProps, ReferenceState, ReferenceSlots } from './Reference.types';
import { useDesignVersion } from '@fluentui-copilot/react-provider';
import type { JSXIntrinsicElement } from '@fluentui/react-utilities';
/**
 * Create the state required to render Reference.
 *
 * The returned state can be modified with hooks such as useReferenceStyles_unstable,
 * before being passed to renderReference_unstable.
 *
 * @param props - props from this instance of Reference
 * @param ref - reference to root HTMLElement of Reference
 */
export const useReference_unstable = (
  props: ReferenceProps,
  ref: React.Ref<HTMLButtonElement | HTMLAnchorElement>,
): ReferenceState => {
  const { as, children, id } = props;

  const designVersion = useDesignVersion(props.designVersion);

  const shouldUseOverflow = useReferenceListContext_unstable(ctx => ctx.shouldUseOverflow);

  const referenceId = useId('reference-', id);

  const elementType = as || ((props as JSXIntrinsicElement<'a'>).href ? 'a' : 'button');
  const propsWithAssignedAs = {
    type: elementType === 'button' ? 'button' : undefined,
    ...props,
    as: elementType,
    id: referenceId,
  };

  const citation = slot.optional(props.citation, { elementType: 'span' });
  const graphic = slot.optional(props.graphic, { elementType: 'span' });
  const content = slot.optional(props.content as ReferenceSlots['content'], {
    elementType: 'span',
    renderByDefault: graphic !== undefined || !!children,
  });
  const divider = slot.optional(props.divider, {
    elementType: 'span',
    renderByDefault: citation !== undefined && content !== undefined,
  });

  const state: ReferenceState = {
    designVersion,
    id: referenceId,
    shouldUseOverflow,

    components: {
      root: elementType,
      citation: 'span',
      divider: 'span',
      graphic: 'span',
      content: 'span',
    },

    root: slot.always(
      getIntrinsicElementProps(
        elementType,
        {
          ref,
          ...propsWithAssignedAs,
        },
        // `content` is a slot and it's type clashes with the HTMLElement `content` attribute
        ['content'],
      ) as ReferenceSlots['root'],
      { elementType },
    ),
    citation,
    divider,
    content,
    graphic,
  };

  if (state.graphic && React.isValidElement<HTMLElement>(state.graphic.children)) {
    state.graphic.children = React.cloneElement(state.graphic.children, {
      className: mergeClasses(state.graphic.children.props.className, referenceExtraClassNames.graphicChild),
    });
  }

  return state;
};
